import { generateClient, get } from 'aws-amplify/api';
import { fetchAuthSession } from "aws-amplify/auth";
import { MDBBtn, MDBBtnGroup, MDBCard, MDBCardBody, MDBCardTitle, MDBIcon, MDBModal, MDBModalBody, MDBModalContent, MDBModalDialog, MDBModalHeader, MDBModalTitle, MDBRow, MDBTable, MDBTableBody, MDBTableHead, MDBTabs, MDBTabsItem, MDBTabsLink, MDBTabsContent, MDBTabsPane, MDBInput, MDBSelect, MDBCol, MDBCheckbox, MDBDatepicker, MDBProgress, MDBProgressBar, MDBSpinner, MDBFile } from "mdb-react-ui-kit";
import { useContext, useEffect, useRef, useState, useCallback } from "react";
import { UserContext } from "../../App";
import { isHDLectureManager, hasHDIndustry } from "../../config/hdLectureManager";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import lectureSchema from '../../validation/LectureValidation';
import {onCreateLecture, onUpdateLecture, onDeleteLecture} from '../../graphql/subscriptions';
import { type } from "@testing-library/user-event/dist/type";
import { getUrl, remove, uploadData } from "aws-amplify/storage";
import ReactPlayer from 'react-player'
import * as XLSX from 'xlsx';

import LectureModal from '../lectures/scheduling/LectureModal';
import LectureForm from "../lectures/LectureForm";
import { isoToGermanDate } from "../../utils/dateTools";
import ReactCountryFlag from "react-country-flag";
import { getLocalizedText } from "../../utils/localizedText";

// Import the enhanced validation system from user lectures
import { validateLectureForEvent, lounges26Schema } from '../../components-user/event-management/lecture-registration/validation/lectures';
import { captureError } from '../../utils/sentryHelpers';

const client = generateClient();
export default function AllLectures() {
    const { t, i18n } = useTranslation();
    const { user } = useContext(UserContext);
    const restrictedHD = isHDLectureManager(user);
    const [basicActive, setBasicActive] = useState('tab1');

    const handleBasicClick = (value) => {
        if (value === basicActive) {
            return;
        }

        setBasicActive(value);
    };

    const [events, setEvents] = useState([]);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [lectures, setLectures] = useState([]);
    const [shownLectures, setShownLectures] = useState([]);
    const [totalLectures, setTotalLectures] = useState(0);

    const [isLectureFormOpen, setIsLectureFormOpen] = useState(false);
    const [editingLecture, setEditingLecture] = useState(null);


    const [validationErrors, setValidationErrors] = useState({});

    /**
     * Enhanced validation using the same system as user lectures
     */
    const validateLectures = useCallback((lecturesToValidate) => {
        const errors = lecturesToValidate.map((lecture) => {
            // Transform lecture data to match validation schema expectations
            const transformedLecture = {
                ...lecture,
                // Transform speakers from unified speakers.items array (already combined from speakers + speakersUsers)
                speakers: (lecture.speakers?.items || [])
                    .filter(item => item && (item.employee || item.user))
                    .map(item => {
                        // Employee-based speaker
                        if (item.employee) {
                            return {
                                id: item.employee.id || '',
                                visavisId: item.employee.id || '',
                                employeeId: item.employee.id || '',
                                speakerType: 'employee',
                                firstName: item.employee.user?.firstName || '',
                                lastName: item.employee.user?.lastName || '',
                                middleName: item.employee.user?.middleName,
                                title: item.employee.user?.title,
                                department: item.employee?.department,
                                image: item.employee?.image,
                                type: item.type || (item.employee.id === lecture.lectureMainSpeakerId ? 'main_speaker' : 'co_speaker'),
                                name: `${item.employee.user?.firstName || ''} ${item.employee.user?.lastName || ''}`.trim(),
                                company: item.employee.company?.name || ''
                            };
                        }
                        // User-based speaker (from speakersUsers)
                        const isMainSpeakerUser = item.user.id === lecture.lectureMainSpeakerUserId;
                        return {
                            id: item.user.id || '',
                            visavisId: item.user.id || '',
                            userId: item.user.id || '',
                            speakerType: 'user',
                            firstName: item.user.firstName || '',
                            lastName: item.user.lastName || '',
                            middleName: item.user.middleName,
                            title: item.user.title,
                            image: item.user.image,
                            type: isMainSpeakerUser ? 'main_speaker' : 'co_speaker',
                            name: `${item.user.firstName || ''} ${item.user.lastName || ''}`.trim(),
                            company: ''
                        };
                    }),
                // Transform topics from objects to string IDs
                // After getLectures, topics is a flat array — but onSave may pass raw GraphQL format {items: [...]}
                topics: (Array.isArray(lecture.topics) ? lecture.topics : (lecture.topics?.items?.map(t => t.topic) ?? [])).map(topic =>
                    topic?.id
                ).filter(id => id),
                // Transform subjects from GraphQL format to validation format
                subjects: lecture.subjects?.map(subject => ({
                    de: subject.name?.find(n => n.language === 'de')?.text || '',
                    en: subject.name?.find(n => n.language === 'en')?.text || ''
                })) || [],
                // Transform conditions to generalConditions if needed
                generalConditions: lecture.conditions?.filter(c => 
                    ['submission_deadlines', 'advertising_free_content', 'recording_policy', 
                     'speaker_expertise', 'program_scheduling', 'file_upload_deadlines'].includes(c.name)
                ).map(c => ({
                    id: c.name,
                    accepted: c.accepted
                })) || [],
                // Ensure handout field exists (combine pdfDe and pdfEn)
                handout: lecture.pdfDe || lecture.pdfEn ? {
                    de: lecture.pdfDe ? {
                        fileName: lecture.pdfDe.fileName || lecture.pdfDe.name,
                        file: lecture.pdfDe
                    } : undefined,
                    en: lecture.pdfEn ? {
                        fileName: lecture.pdfEn.fileName || lecture.pdfEn.name,
                        file: lecture.pdfEn
                    } : undefined
                } : undefined
            };
            
            // Use the new validation system for lectures
            const validationResult = validateLectureForEvent(transformedLecture, lounges26Schema);
            
            let lectureErrors = [];
            
            if (!validationResult.success) {
                lectureErrors = validationResult.errors.map(error => ({
                    field: error.field,
                    message: error.message
                }));
            }

            return {
                id: lecture.id,
                errors: lectureErrors
            };
        });
        
        // Convert array to object keyed by lecture ID for easier lookup
        const errorsObj = {};
        errors.forEach(error => {
            if (error.errors.length > 0) {
                errorsObj[error.id] = error.errors;
            }
        });
        
        setValidationErrors(errorsObj);
        return errorsObj;
    }, []);

    /**
     * Get validation errors for a specific lecture
     */
    const getLectureValidationErrors = useCallback((lectureId) => {
        return validationErrors[lectureId] || [];
    }, [validationErrors]);

    /**
     * Check if a lecture is valid (has no validation errors)
     */
    const isLectureValid = useCallback((lectureId) => {
        const errors = getLectureValidationErrors(lectureId);
        return errors.length === 0;
    }, [getLectureValidationErrors]);

    useEffect(() => {
        if(lectures.length > 0) {
            setTimeout(() => {
                validateLectures(lectures)
            }, 500);
        }
    }, [lectures, validateLectures]);

    const [isLoading, setIsLoading] = useState(false);

    const [searchParams, setSearchParams] = useSearchParams();
    const extractExistingParams = (searchParams) => {
        return Array.from(searchParams.entries()).reduce((acc, [key, value]) => {
            if (value === 'true') {
                acc[key] = true;
            } else if (value === 'false') {
                acc[key] = false;
            } else {
                acc[key] = value;
            }
            return acc;
        }, {});
    }

    const [filters, setFilters] = useState({
        approved: null,
        createdAtStart: "2023-12-01T00:00:00.000Z",
        createdAtEnd: null,
        createdAtSort: "desc",
        status: null,
        type: null
    });
    const [lecturesLoaded, setLecturesLoaded] = useState(false);
    const [sortByRoom, setSortByRoom] = useState(false);

    // Sortierung nach Raum/Uhrzeit wenn aktiviert
    useEffect(() => {
        if (lectures.length === 0) return;

        if (sortByRoom) {
            const sortedLectures = [...lectures].sort((a, b) => {
                if (!a.room && !b.room) return 0;
                if (!a.room) return 1;
                if (!b.room) return -1;

                if (a.room !== b.room) {
                    return a.room.localeCompare(b.room);
                }

                if (!a.startTime && !b.startTime) return 0;
                if (!a.startTime) return 1;
                if (!b.startTime) return -1;
                return new Date(a.startTime) - new Date(b.startTime);
            });
            setShownLectures(sortedLectures);
        } else {
            setShownLectures(lectures);
        }
    }, [sortByRoom, lectures]);

    const getLectures = async (eventId) => {
        try {
            setIsLoading(true);
            const searchLectureFilter = {
                eventLecturesId: { eq: eventId }
            };
            const searchLectureSort = {};
            if (typeof filters.approved === "boolean") {
                searchLectureFilter.approved = { eq: filters.approved };
            }
            if (filters.createdAtStart && filters.createdAtEnd) {
                searchLectureFilter.and = [{createdAt: {gt: filters.createdAtStart}}, {createdAt: {lt: filters.createdAtEnd}}];
            } else if (filters.createdAtStart) {
                searchLectureFilter.createdAt = { gt: filters.createdAtStart };
            } else if (filters.createdAtEnd) {
                searchLectureFilter.createdAt = { lt: filters.createdAtEnd };
            }
            if (filters.createdAtSort) {
                searchLectureSort.field = "createdAt";
                searchLectureSort.direction = filters.createdAtSort;
            }
            if (filters.updatedAtSort) {
                searchLectureSort.field = "updatedAt";
                searchLectureSort.direction = filters.updatedAtSort;
            }
            if (filters.status) {
                if (filters.status === "created") {
                    // "created" soll auch Vorträge mit status null einschließen
                    searchLectureFilter.or = [
                        { status: { eq: "created" } },
                        { status: { exists: false } }
                    ];
                } else {
                    searchLectureFilter.status = { eq: filters.status };
                }
            }
            if (filters.type) {
                searchLectureFilter.type = { eq: filters.type };
            }

            let allItems = [];
            let currentNextToken = null;
            let totalCount = 0;

            do {
            const lectures = await client.graphql({
                variables: {
                    filter: searchLectureFilter,
                    sort: searchLectureSort,
                    limit: 1000,
                    nextToken: currentNextToken
                },
                query: /* GraphQL */ `
                query SearchLectures(
                  $filter: SearchableLectureFilterInput
                  $sort: [SearchableLectureSortInput]
                  $limit: Int
                  $nextToken: String
                  $from: Int
                  $aggregates: [SearchableLectureAggregationInput]
                ) {
                  searchLectures(
                    filter: $filter
                    sort: $sort
                    limit: $limit
                    nextToken: $nextToken
                    from: $from
                    aggregates: $aggregates
                  ) {
                    items {
                        title {
                            language
                            text
                        }
                        event {
                            id
                            startDate
                            lectureEditEndDate
                            lectureDocumentsEndDate
                            lecturePresentationEndDate
                            name {
                                language
                                text
                            }
                        }
                        speakers {
                            items {
                                id
                                employee {
                                    id
                                    image {
                                    fileName
                                    alt
                                    }
                                    contact {
                                        email
                                        phone
                                        mobile
                                    }
                                    user {
                                        title
                                        firstName
                                        lastName
                                        middleName
                                        email
                                        contact {
                                            email
                                            phone
                                            mobile
                                        }
                                    }
                                    company {
                                        name
                                    }
                                }
                            }
                        }
                        speakersUsers {
                            items {
                                user {
                                    id
                                    title
                                    firstName
                                    lastName
                                    middleName
                                    email
                                    image {
                                    fileName
                                    alt
                                    }
                                    contact {
                                        email
                                        phone
                                        mobile
                                    }
                                }
                            }
                        }
                        mainSpeakerUser {
                            id
                            image {
                              fileName
                              alt
                              }
                              title
                              firstName
                              lastName
                              middleName
                              email
                              contact {
                                  email
                                  phone
                                  mobile
                              }
                        }
                        subjects {
                            id
                            name {
                                language
                                text
                            }
                        } 
                        topics {
                            items {
                              topic {
                                id
                                name {
                                  language
                                  text
                                }
                              }
                            }
                        }        
                        content {
                            language
                            text
                        }
                        conditions {
                            name
                            accepted
                        }
                        image {
                            fileName
                            alt
                        }
                        pdfDe {
                            fileName
                            s3Path
                            url
                            name
                            updatedAt
                        }
                        pdfEn {
                            fileName
                            s3Path
                            url
                            name
                            updatedAt
                        }
                        presentation {
                            fileName
                            s3Path
                            fileName
                            updatedAt
                        }
                        videoS3Key {
                            s3Path
                        }
                        company {
                            id
                            name
                            image {
                                main {
                                    fileName
                                    alt
                                }
                            }
                            adminEmployees {
                                items {
                                    id
                                    contact {
                                        email
                                        phone
                                        mobile
                                    }
                                    user {
                                        title
                                        firstName
                                        lastName
                                        email
                                        contact {
                                            email
                                            phone
                                            mobile
                                        }
                                    }
                                }
                            }
                        }
                        conditions {
                            name
                            accepted
                        }
                        subjects {
                            id
                            name {
                                language
                                text
                            }
                        }
                        content {
                            language
                            text
                        }
                        id
                        adminComment
                        companyBooth
                        session
                        room
                        startTime
                        endTime
                        type
                        presentationLanguage
                        createdAt
                        updatedAt
                        companyLecturesId
                        eventLecturesId
                        lectureMainSpeakerId
                        lectureMainSpeakerUserId
                        approved
                        status
                        __typename
                    }
                    nextToken
                    total
                    aggregateItems {
                      name
                      result {
                        ... on SearchableAggregateScalarResult {
                          value
                        }
                        ... on SearchableAggregateBucketResult {
                          buckets {
                            key
                            doc_count
                            __typename
                          }
                        }
                      }
                      __typename
                    }
                    __typename
                  }
                }
              `
            });

                allItems = [...allItems, ...lectures.data.searchLectures.items];
                currentNextToken = lectures.data.searchLectures.nextToken;
                totalCount = lectures.data.searchLectures.total;
            } while (currentNextToken);

            const lecturesData = allItems.map(lecture => ({
                ...lecture,
                topics: lecture.topics?.items?.map(topic => topic.topic) ?? [],
                // Combine speakers and speakersUsers into a unified speakers array
                // Filter out corrupted data: null items or items with null employee from DB
                speakers: {
                    items: [
                        // Filter out null items and speakers with null employee (data corruption in DB)
                        ...(lecture.speakers?.items || []).filter(item => item != null && item.employee != null),
                        ...(lecture.speakersUsers?.items || []).filter(item => item != null && item.user != null).map(item => ({
                            // Transform speakerUser to match speaker structure
                            employee: null,
                            user: item.user
                        }))
                    ]
                }
            }));
            setLectures(lecturesData);
            setShownLectures(lecturesData);
            setTotalLectures(totalCount);
            setLecturesLoaded(true);
            setIsLoading(false);
        } catch (error) {
            console.log("error on fetching lectures", error);
            // Only log to Sentry if there's no partial data (real error, not just partial GraphQL errors)
            if (!error.data?.searchLectures?.items) {
                captureError(error, {
                    feature: 'event-management-admin',
                    operation: 'fetch-lectures',
                    consoleMessage: 'error on fetching lectures',
                    context: {}
                });
            }
            // GraphQL may return partial data with errors (e.g., when some nested fields fail)
            // In this case, we still want to display the lectures that were successfully fetched
            if (error.data?.searchLectures?.items) {
                const lectureData = error.data.searchLectures.items.filter(lecture => lecture != null).map(lecture => ({
                    ...lecture,
                    topics: lecture.topics?.items?.map(topic => topic.topic) ?? [],
                    // Clean up null items and speakers with null employee (same as success case)
                    speakers: {
                        items: [
                            ...(lecture.speakers?.items || []).filter(item => item != null && item.employee != null),
                            ...(lecture.speakersUsers?.items || []).filter(item => item != null && item.user != null).map(item => ({
                                employee: null,
                                user: item.user
                            }))
                        ]
                    }
                }));
                setLectures(lectureData);
                setShownLectures(lectureData);
                setTotalLectures(error.data.searchLectures.total || lectureData.length);
                setLecturesLoaded(true);
            }
            setIsLoading(false);
        }
    };
    const [searchTerm, setSearchTerm] = useState("");

    const handleInputSearch = (e) => {
        setSearchTerm(e.target.value);
        searchLectures(e.target.value);
    }

    const searchLectures = (searchTerm) => {
        console.log("searchTerm", searchTerm);
        setSearchParams(prev => {
            const newParams = new URLSearchParams(prev);
            if (searchTerm.length > 0) {
                newParams.set('search', searchTerm);
            } else {
                newParams.delete('search');
            }
            return newParams;
        });
        if (searchTerm.length > 0) {
            setShownLectures(lectures.filter(lecture => {
                if (lecture.company?.name?.toLowerCase()?.includes(searchTerm.toLowerCase())) {
                    return true;
                }
                if (lecture.title?.find(t => t.language === i18n.language)?.text?.toLowerCase()?.includes(searchTerm.toLowerCase())) {
                    return true;
                }
                return false;
            }));
        } else {
            setShownLectures(lectures);
        }
    }

    useEffect(() => {
        const searchTerm = searchParams.get("search");
        if (lecturesLoaded && searchTerm) {
            handleInputSearch({target: {value: searchTerm}});
        }
    }, [lecturesLoaded]);

    const getEvents = async () => {
        try {
            const events = await client.graphql({
                query: /* GraphQL */ `
                query ListEvents(
                  $filter: ModelEventFilterInput
                  $limit: Int
                  $nextToken: String
                ) {
                  listEvents(filter: $filter, limit: $limit, nextToken: $nextToken) {
                        items {
                            id
                            name {
                                language
                                text
                            }
                            startDate
                            lectureRegistrationStartDate
                            endDate
                            status
                            industries {
                                items {
                                    industry {
                                        id
                                    }
                                }
                            }
                        }
                    }
                }
                `,
                variables: {
                    filter: {
                        // lectureRegistrationStartDate: { le: new Date().toISOString().substring(0, 10) },
                        lectureRooms: { gt: 0 },
                        startDate: { ge: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString().substring(0, 10) }
                        // status: { eq: "active" }
                    }
                }
            });
            // Sort events by start date
            let items = events.data.listEvents.items;
            if (restrictedHD) {
                items = items.filter(hasHDIndustry);
            }
            const sortedEvents = items.sort((a, b) =>  new Date(b.startDate) - new Date(a.startDate));
            setEvents(sortedEvents);
            return sortedEvents;
        } catch (error) {
            console.log("error on fetching events", error);
            captureError(error, {
                feature: 'event-management-admin',
                operation: 'fetch-events',
                consoleMessage: 'error on fetching events',
                context: {}
            });
        }
    };


    const [filtersInitialized, setFiltersInitialized] = useState(false);

useEffect(() => {
    // Beim ersten Render die Filter aus den searchParams extrahieren und setzen
    const extractedParams = extractExistingParams(searchParams);
    setFilters(oldFilters => ({ ...extractedParams, createdAtStart: extractedParams.createdAtStart || oldFilters.createdAtStart , createdAtSort: extractedParams.createdAtSort || oldFilters.createdAtSort }));
    setFiltersInitialized(true); // Setze auf true, sobald die Initialisierung abgeschlossen ist
}, []); // Leere Abhängigkeiten, um den Hook nur einmal auszuführen

const [eventsLoaded, setEventsLoaded] = useState(false);

const loadEvents = async () => {
    const eventsData = await getEvents();
    setEvents(eventsData);
    setEventsLoaded(true);
}

// Separater useEffect: Reagiert auf URL-Änderungen (z.B. Browser "Zurück")
// und synchronisiert das ausgewählte Event mit den URL-Parametern
useEffect(() => {
    if (!eventsLoaded || events.length === 0) {
        return; // Warten bis Events geladen sind
    }

    const eventIdFromParams = searchParams.get("event");
    if (eventIdFromParams && events.find(event => event.id === eventIdFromParams)) {
        // Event aus URL-Params existiert -> auswählen
        if (selectedEvent !== eventIdFromParams) {
            setSelectedEvent(eventIdFromParams);
        }
    } else if (!selectedEvent) {
        // Kein Event in Params und noch keines ausgewählt -> erstes Event wählen
        setSelectedEvent(events[0].id);
    }
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [searchParams, events, eventsLoaded]); // selectedEvent absichtlich ausgelassen um Endlosschleife zu vermeiden

useEffect(() => {
    if (filtersInitialized) {
        // Aktualisiere die searchParams und rufe getLectures auf, nachdem die Filter initialisiert wurden
        const params = Object.entries(filters).reduce((acc, [key, value]) => {
            if (value !== null) {
                acc[key] = value;
            }
            return acc;
        }, {});

        setSearchParams(params);
        if(selectedEvent) {
            getLectures(selectedEvent);
          }
    }
    if(events.length === 0) {
        loadEvents();
    }
}, [filters, filtersInitialized]); // Abhängigkeit von den Filters und filtersInitialized

useEffect(() => {
  if(selectedEvent) {
    getLectures(selectedEvent);
  }
}, [selectedEvent])

const handleEventTabClick = (eventId) => {
    setSelectedEvent(eventId);
    setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.set('event', eventId);
        return newParams;
    });
};

    // subscribe to lecture changes


// Subscribe to update of Todo
// const updateSub = client
//   .graphql({ query: onUpdateLecture })
//   .subscribe({
//     next: ({ data }) => console.log("update", data),
//     error: (error) => console.warn(error)
//   });

const updateLectureOnSubscription = (lecture) => {
    if (!lecture) {
        return;
    }
    // Transform topics from GraphQL format to flat array (same as in getLectures)
    const transformedLecture = {
        ...lecture,
        topics: lecture.topics?.items?.map(topic => topic.topic) ?? [],
    };
    setLectures(currentLectures => currentLectures.map(l => l.id === transformedLecture.id ? transformedLecture : l));
    setShownLectures(currentShownLectures => currentShownLectures.map(l => l.id === transformedLecture.id ? transformedLecture : l));
};

const subscribeUpdateLecture = () => {
    console.log("subscribe update lecture")
    const subscription = client.graphql({
        query: /* GraphQL */ `
        subscription OnUpdateLecture($filter: ModelSubscriptionLectureFilterInput) {
          onUpdateLecture(filter: $filter) {
            title {
                language
                text
            }
            speakers {
                items {
                    employee {
                        image {
                        fileName
                        alt
                        }
                    }
                }
            }
            subjects {
                id
                name {
                    language
                    text
                }
            } 
            topics {
                items {
                  topic {
                    id
                    name {
                      language
                      text
                    }
                  }
                }
            }        
            content {
                language
                text
            }
            conditions {
                name
                accepted
            }
            image {
                fileName
                alt

            }
            pdfDe {
                url
                fileName
                name
                updatedAt
            }
            pdfEn {
                url
                fileName
                name
                updatedAt
            }
            presentation {
                fileName
                name
                updatedAt
            }
            company {
                id
                name
                image {
                    main {
                        fileName
                        alt
                    }
                }
            }
            conditions {
                name
                accepted
            }
            id
            adminComment
            session
            companyBooth
            room
            type
            presentationLanguage
            createdAt
            updatedAt
            companyLecturesId
            eventLecturesId
            lectureMainSpeakerId
            lectureMainSpeakerUserId
            approved
            status
            __typename
          }
        }
        `
    }).subscribe({
        next: ({ data }) => {
            // Update the lecture in the list of lectures, found by data.onUpdateLecture.id
            console.log("update", data);
            //getLectures();
            updateLectureOnSubscription(data.onUpdateLecture);

        },
        error: (error) => console.warn(error)
    });

    return {
        unsubscribe: () => subscription.unsubscribe()
    };
};

useEffect(() => {
    // Starten des Abonnements
    const subscription = subscribeUpdateLecture();

    // Bereinigungsfunktion
    return () => {
        // Beenden des Abonnements
        subscription.unsubscribe();
    };
}, []);


  // Stop receiving data updates from the subscription

    


    const [deleteLectureModal, setDeleteLectureModal] = useState(false);
    const toggleDeleteLectureModal = () => setDeleteLectureModal(!deleteLectureModal);

    const [selectedLectureRemove, setSelectedLectureRemove] = useState(null);

    const deleteLecture = async (id) => {
        try {
            const deletedLecture = await client.graphql({
                variables: {
                    input: {
                        id: id
                    }
                },
                query: /* GraphQL */ `
                mutation DeleteLecture(
                  $input: DeleteLectureInput!
                  $condition: ModelLectureConditionInput
                ) {
                  deleteLecture(input: $input, condition: $condition) {
                    id
                  }
                }
              `
            });
            console.log("deleted lecture", deletedLecture);
            setLectures(lectures.filter((lecture) => lecture.id !== id));
            setShownLectures(shownLectures.filter((lecture) => lecture.id !== id));
            setSelectedLectureRemove(null);
            toggleDeleteLectureModal();
        } catch (error) {
            console.log("error on deleting lecture", error);
            captureError(error, {
                feature: 'event-management-admin',
                operation: 'delete-lecture',
                consoleMessage: 'error on deleting lecture',
                context: { lectureId: id }
            });
        }
    }

    


    const filterbyHasErrors = (selectedErrors) => { // selectedErrors = ["Titel", "Referenten"]
        console.log(selectedErrors);
        if (selectedErrors.length > 0) {
            setShownLectures(lectures.filter(lecture => {
                const lectureErrors = validationErrors[lecture.id];
                if (lectureErrors && lectureErrors.length > 0) {
                    return lectureErrors.some(error => selectedErrors.includes(error.message));
                }
                return false;
            }));
        } else {
            setShownLectures(lectures);
        }
    }

    // Video Upload, View and Remove Modal
    const [videoModal, setVideoModal] = useState(false);
    const [selectedLectureVideo, setSelectedLectureVideo] = useState(null);
    const [selectedVideoUrl, setSelectedVideoUrl] = useState(null);
    const [lectureVideosUploadStatus, setLectureVideosUploadStatus] = useState({});
    const hanleOpenVideoModal = async (lectureId) => {
        setSelectedLectureVideo(lectureId);
        const videoKey = lectures.find(l => l.id === lectureId)?.videoS3Key?.s3Path;
        if (videoKey) {
            const videoUrl = await getVideoUrl(videoKey);
            setSelectedVideoUrl(videoUrl);
        }
        toggleVideoModal();
    }
    const toggleVideoModal = () => setVideoModal(!videoModal);

    const inputFieldRef = useRef();

    const uploadVideo = async (file, lectureId) => {
        try {
            let lastTimeStamp = Date.now(); // Initial timestamp
            let lastTransferredBytes = 0; // Initial transferred bytes

            const result = await uploadData({
              key: `lectureVideos/${lectureId}.mp4`, 
              data: file,
              options: {
                onProgress: ({ transferredBytes, totalBytes }) => {
                  if (totalBytes) {
                    const currentTime = Date.now();
                    const timeElapsed = (currentTime - lastTimeStamp) / 1000; // Convert ms to seconds
                    const bytesTransferred = transferredBytes - lastTransferredBytes;
                    const speedInMBps = (bytesTransferred / (1024 * 1024)) / timeElapsed; // Convert bytes to MB and divide by seconds
                    setLectureVideosUploadStatus(old => ({
                        ...old,
                      [lectureId]: {
                        transferredBytes,
                        totalBytes,
                        percent: Math.round((transferredBytes / totalBytes) * 100),
                        speed: speedInMBps.toFixed(2) + ' MB/s',
                      },
                    }));
                  }
                }
              }
            }).result;
            console.log('Path from Response: ', result.key);
            // Update the lecture with the video path on the field videoS3Key
            setLectures(lectures.map(l => l.id === lectureId ? {...l, videoS3Key: {s3Path: result.key}} : l));
            setShownLectures(shownLectures.map(l => l.id === lectureId ? {...l, videoS3Key: {s3Path: result.key}} : l));
            setSelectedVideoUrl(await getVideoUrl(result.key));
            await client.graphql({
                variables: {
                    input: {
                        id: lectureId,
                        videoS3Key: {
                            s3Path: result.key
                        }
                    }
                },
                query: /* GraphQL */ `
                mutation UpdateLecture(
                  $input: UpdateLectureInput!
                ) {
                  updateLecture(input: $input) {
                    id
                    videoS3Key {
                        s3Path
                    }
                  }
                }
              `
            });
          } catch (error) {
            console.log('Error : ', error);
            captureError(error, {
                feature: 'event-management-admin',
                operation: 'update-lecture-recording-confirmation',
                consoleMessage: 'Error',
                context: {}
            });
          }
    }

    const getVideoUrl = async (videoKey) => {
        try {
            const result = await getUrl({
                key: videoKey,
                level: 'public'
            });
            console.log("video url", result.url.href);
            return result.url.href;
        } catch (error) {
            console.log("error on getting video url", error);
            captureError(error, {
                feature: 'event-management-admin',
                operation: 'get-video-url',
                consoleMessage: 'error on getting video url',
                context: {}
            });
        }
    }

    const removeVideo = async (lectureId) => {
        try {
            const videoKey = lectures.find(l => l.id === lectureId)?.videoS3Key?.s3Path;
            if (videoKey) {
                await remove({
                    key: videoKey
                });
                setLectures(lectures.map(l => l.id === lectureId ? {...l, videoS3Key: null} : l));
                setShownLectures(shownLectures.map(l => l.id === lectureId ? {...l, videoS3Key: null} : l));
                await client.graphql({
                    variables: {
                        input: {
                            id: lectureId,
                            videoS3Key: null
                        }
                    },
                    query: /* GraphQL */ `
                    mutation UpdateLecture(
                      $input: UpdateLectureInput!
                    ) {
                      updateLecture(input: $input) {
                        id
                        videoS3Key {
                            s3Path
                        }
                      }
                    }
                  `
                });
                setSelectedVideoUrl(null);
            }
        } catch (error) {
            console.log("error on removing video", error);
            captureError(error, {
                feature: 'event-management-admin',
                operation: 'remove-video',
                consoleMessage: 'error on removing video',
                context: {}
            });
        }
    }

    // Hilfsfunktion um alle Referenten aus beiden Quellen zu sammeln
    const getSpeakersArray = (lecture) => {
        const speakers = [];

        // Employees als Referenten (speakers.items)
        if (lecture.speakers?.items && lecture.speakers.items.length > 0) {
            lecture.speakers.items.forEach(item => {
                if (item.employee) {
                    const employee = item.employee;
                    const user = employee.user;

                    // Kontaktdaten: Priorität employee.contact, dann user.email, dann user.contact
                    const email = employee.contact?.email || user?.email || user?.contact?.email || '';
                    const phone = employee.contact?.phone || employee.contact?.mobile || user?.contact?.phone || user?.contact?.mobile || '';

                    speakers.push({
                        title: user?.title || '',
                        firstName: user?.firstName || '',
                        lastName: user?.lastName || '',
                        email: email,
                        phone: phone
                    });
                }
            });
        }

        // Users als Referenten (speakersUsers.items)
        if (lecture.speakersUsers?.items && lecture.speakersUsers.items.length > 0) {
            lecture.speakersUsers.items.forEach(item => {
                if (item.user) {
                    const user = item.user;

                    // Kontaktdaten aus user.email oder user.contact
                    const email = user.email || user.contact?.email || '';
                    const phone = user.contact?.phone || user.contact?.mobile || '';

                    speakers.push({
                        title: user.title || '',
                        firstName: user.firstName || '',
                        lastName: user.lastName || '',
                        email: email,
                        phone: phone
                    });
                }
            });
        }

        // Fallback: mainSpeakerUser, falls keine anderen Speaker vorhanden
        if (speakers.length === 0 && lecture.mainSpeakerUser) {
            const user = lecture.mainSpeakerUser;
            const email = user.email || user.contact?.email || '';
            const phone = user.contact?.phone || user.contact?.mobile || '';

            speakers.push({
                title: user.title || '',
                firstName: user.firstName || '',
                lastName: user.lastName || '',
                email: email,
                phone: phone
            });
        }

        return speakers;
    };

    // Hilfsfunktion um alle Admins der Company zu sammeln
    const getAdminsArray = (lecture) => {
        const admins = [];

        // Admin Employees aus company.adminEmployees.items
        if (lecture.company?.adminEmployees?.items && lecture.company.adminEmployees.items.length > 0) {
            lecture.company.adminEmployees.items.forEach(employee => {
                if (employee) {
                    const user = employee.user;

                    // Kontaktdaten: Priorität employee.contact, dann user.email, dann user.contact
                    const email = employee.contact?.email || user?.email || user?.contact?.email || '';
                    const phone = employee.contact?.phone || employee.contact?.mobile || user?.contact?.phone || user?.contact?.mobile || '';

                    admins.push({
                        title: user?.title || '',
                        firstName: user?.firstName || '',
                        lastName: user?.lastName || '',
                        email: email,
                        phone: phone
                    });
                }
            });
        }

        return admins;
    };

    const handleDownloadExcel = () => {
        // Excel-Daten vorbereiten
        const excelData = [];

        // Maximale Anzahl an Referenten ermitteln für dynamische Spalten
        let maxSpeakers = 0;
        let maxAdmins = 0;
        shownLectures.forEach(lecture => {
            const speakerCount = getSpeakersArray(lecture).length;
            if (speakerCount > maxSpeakers) {
                maxSpeakers = speakerCount;
            }

            const adminCount = getAdminsArray(lecture).length;
            if (adminCount > maxAdmins) {
                maxAdmins = adminCount;
            }
        });

        // Header dynamisch erstellen
        const headers = [
            'Erstellungsdatum',
            'Vortragsart',
            'Firma',
            'Titel',
            'Titel EN',
            'Link',
            'Status',
            'Fehler',
            'Admin Comment',
            'Präsentation Tag',
            'Präsentation Uhrzeit',
            'Präsentation Raum',
            'Sprache',
            'ECV Kommentar',
            'Podcast',
            'ECV',
            'Unterpunkt_1',
            'Unterpunkt_2',
            'Unterpunkt_3',
            'Unterpunkt_4',
            'Unterpunkt_5',
            'Beschreibung',
        ];

        // Referenten-Spalten hinzufügen (Titel, Vorname, Nachname, Email, Telefon)
        for (let i = 1; i <= maxSpeakers; i++) {
            headers.push(`Referent_${i}_Titel`);
            headers.push(`Referent_${i}_Vorname`);
            headers.push(`Referent_${i}_Nachname`);
            headers.push(`Referent_${i}_Email`);
            headers.push(`Referent_${i}_Telefon`);
        }

        // Admin-Spalten hinzufügen (Titel, Vorname, Nachname, Email, Telefon)
        for (let i = 1; i <= maxAdmins; i++) {
            headers.push(`Admin_${i}_Titel`);
            headers.push(`Admin_${i}_Vorname`);
            headers.push(`Admin_${i}_Nachname`);
            headers.push(`Admin_${i}_Email`);
            headers.push(`Admin_${i}_Telefon`);
        }

        excelData.push(headers);

        // Daten für jeden Vortrag hinzufügen
        shownLectures.forEach(lecture => {
            const errors = validationErrors[lecture.id]?.map(e => e.message)?.join(', ') || '';

            // Datum und Uhrzeit formatieren, falls vorhanden
            const presentationDate = lecture.startTime ? new Date(lecture.startTime).toLocaleDateString('de-DE') : '';
            const presentationTime = lecture.startTime ? new Date(lecture.startTime).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) : '';

            // Alle Referenten sammeln
            const speakers = getSpeakersArray(lecture);
            // Alle Admins sammeln
            const admins = getAdminsArray(lecture);

            // Unterpunkte sammeln (deutsche Variante)
            const subjects = lecture.subjects?.map(subject =>
                subject.name?.find(n => n.language === 'de')?.text || ''
            ) || [];

            // Beschreibung (deutsche Variante)
            const contentText = lecture.content?.find(c => c.language === 'de')?.text || '';

            const row = [
                new Date(lecture.createdAt).toLocaleDateString('de-DE'),
                t(lecture.type),
                lecture.company?.name || '',
                lecture.title?.find(t => t.language === 'de')?.text || '',
                lecture.title?.find(t => t.language === 'en')?.text || '',
                {
                    v: `Anzeigen`,
                    l: {
                        Target: `https://app.cleanroom-processes.de/admin/lecture/${lecture.company?.id}/${lecture.id}`,
                        Tooltip: "Click to open lecture"
                    }
                },
                t(lecture.status === "checked" ? "Freigegeben" : lecture.status),
                errors,
                lecture.adminComment || '',
                presentationDate,
                presentationTime,
                lecture.room || '',
                lecture.presentationLanguage || '',
                lecture.conditions?.find(condition => condition.name === "ecv")?.comment || '',
                lecture.conditions?.find(condition => condition.name === "podcast")?.accepted ? "Ja" : "Nein",
                lecture.conditions?.find(condition => condition.name === "ecv")?.accepted ? "Ja" : "Nein",
                // 5 Unterpunkt-Spalten
                subjects[0] || '',
                subjects[1] || '',
                subjects[2] || '',
                subjects[3] || '',
                subjects[4] || '',
                contentText,
            ];

            // Referenten-Spalten hinzufügen (separate Spalten für jedes Feld)
            for (let i = 0; i < maxSpeakers; i++) {
                if (i < speakers.length) {
                    const speaker = speakers[i];
                    row.push(speaker.title || '');
                    row.push(speaker.firstName || '');
                    row.push(speaker.lastName || '');
                    row.push(speaker.email || '');
                    row.push(speaker.phone || '');
                } else {
                    // Leere Zellen für nicht vorhandene Referenten
                    row.push('', '', '', '', '');
                }
            }

            // Admin-Spalten hinzufügen (separate Spalten für jedes Feld)
            for (let i = 0; i < maxAdmins; i++) {
                if (i < admins.length) {
                    const admin = admins[i];
                    row.push(admin.title || '');
                    row.push(admin.firstName || '');
                    row.push(admin.lastName || '');
                    row.push(admin.email || '');
                    row.push(admin.phone || '');
                } else {
                    // Leere Zellen für nicht vorhandene Admins
                    row.push('', '', '', '', '');
                }
            }

            excelData.push(row);
        });

        // Arbeitsmappe erstellen
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.aoa_to_sheet(excelData);

        // Spaltenbreiten automatisch anpassen
        const colWidths = [
            { wch: 12 },  // Created At
            { wch: 15 },  // Type
            { wch: 30 },  // Company
            { wch: 40 },  // Title DE
            { wch: 40 },  // Title EN
            { wch: 10 },  // Link
            { wch: 15 },  // Status
            { wch: 40 },  // Validation Errors
            { wch: 30 },  // Admin Comment
            { wch: 15 },  // Presentation Day
            { wch: 15 },  // Presentation Time
            { wch: 15 },  // Presentation Room
            { wch: 15 },  // Language
            { wch: 15 },  // ECV Comment
            { wch: 10 },  // Podcast
            { wch: 10 },  // ECV
            { wch: 50 },  // Unterpunkt_1
            { wch: 50 },  // Unterpunkt_2
            { wch: 50 },  // Unterpunkt_3
            { wch: 50 },  // Unterpunkt_4
            { wch: 50 },  // Unterpunkt_5
            { wch: 80 },  // Beschreibung
        ];

        // Spaltenbreiten für Referenten hinzufügen (Titel, Vorname, Nachname, Email, Telefon)
        for (let i = 0; i < maxSpeakers; i++) {
            colWidths.push({ wch: 10 }); // Titel
            colWidths.push({ wch: 20 }); // Vorname
            colWidths.push({ wch: 20 }); // Nachname
            colWidths.push({ wch: 30 }); // Email
            colWidths.push({ wch: 20 }); // Telefon
        }

        // Spaltenbreiten für Admins hinzufügen (Titel, Vorname, Nachname, Email, Telefon)
        for (let i = 0; i < maxAdmins; i++) {
            colWidths.push({ wch: 10 }); // Titel
            colWidths.push({ wch: 20 }); // Vorname
            colWidths.push({ wch: 20 }); // Nachname
            colWidths.push({ wch: 30 }); // Email
            colWidths.push({ wch: 20 }); // Telefon
        }

        ws['!cols'] = colWidths;

        // Zeilenhöhen festlegen - Standardhöhe für alle Zeilen (15 = ca. 20px, Excel default)
        // Dies sorgt dafür, dass mehrzeilige Inhalte eingeklappt erscheinen
        if (!ws['!rows']) ws['!rows'] = [];
        for (let i = 0; i < excelData.length; i++) {
            ws['!rows'][i] = { hpt: 15 }; // hpt = height in points
        }

        // Text Wrapping für Zellen mit mehrzeiligem Inhalt aktivieren
        const range = XLSX.utils.decode_range(ws['!ref']);
        for (let R = range.s.r; R <= range.e.r; ++R) {
            for (let C = range.s.c; C <= range.e.c; ++C) {
                const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
                if (!ws[cellAddress]) continue;
                if (!ws[cellAddress].s) ws[cellAddress].s = {};
                ws[cellAddress].s.alignment = { wrapText: true, vertical: 'top' };
            }
        }
        if(!ws['!links']) ws['!links'] = [];
        shownLectures.forEach((_, idx) => {
            const cellRef = XLSX.utils.encode_cell({r: idx + 1, c: 5}); // 5 ist der Index der Link-Spalte
            ws['!links'].push([cellRef, ws[cellRef].l]);
        });
        // Worksheet zur Arbeitsmappe hinzufügen
        XLSX.utils.book_append_sheet(wb, ws, 'Lectures');

        // Event Name für Dateinamen holen
        const eventName = getLocalizedText(events.find(e => e.id === selectedEvent)?.name) || 'Lectures';

        // Excel-Datei herunterladen
        XLSX.writeFile(wb, `${eventName}-Lectures.xlsx`);
    };


    // Document Upload Modal
    const [documentUploadModal, setDocumentUploadModal] = useState(false);
    const [selectedLectureForDocuments, setSelectedLectureForDocuments] = useState(null);
    const toggleDocumentUploadModal = () => setDocumentUploadModal(!documentUploadModal);
    const [presentationUploadModal, setPresentationUploadModal] = useState(false);
    const togglePresentationUploadModal = () => setPresentationUploadModal(!presentationUploadModal);
    
    const handleOpenDocumentUploadModal = (lectureId) => {
        setSelectedLectureForDocuments(lectureId);
        toggleDocumentUploadModal();
    };

    const handleOpenPresentationUploadModal = (lectureId) => {
        setSelectedLectureForDocuments(lectureId);
        togglePresentationUploadModal();
    };

    // File upload states
    const [isUploadingLectureFiles, setIsUploadingLectureFiles] = useState({
        De: false,
        En: false,
        Presentation: false
    });
    
    const [uploadingLecturesErrors, setUploadingLecturesErrors] = useState({
        De: null,
        En: null,
        Presentation: null
    });

    const handleSetFile = async (file, fileType) => {
            if (!file || !selectedLectureForDocuments) return;
            
            try {
                // fileType can be 'De', 'En', or 'Presentation'
                setIsUploadingLectureFiles({...isUploadingLectureFiles, [fileType]: true});
                
                // Upload file to S3
                const result = await uploadData({
                    key: file.name,
                    data: file,
                    options: {
                        accessLevel: 'protected'
                    }
                }).result;
                
                console.log('Upload succeeded: ', result);
                
                // Get the file URL
                const fileURL = await getUrl({ 
                    key: result.key, 
                    options: { level: 'protected' } 
                });
                
                // Update the lecture with the file info
                const identityId = (await fetchAuthSession()).identityId;
                
                // Determine which field to update based on fileType
                let updateField = {};
                if (fileType === 'De' || fileType === 'En') {
                    updateField[`pdf${fileType}`] = {
                        fileName: result.key,
                        name: result.key,
                        s3Path: `protected/${identityId}/${result.key}`,
                        updatedAt: new Date().toISOString()
                    };
                } else if (fileType === 'Presentation') {
                    updateField.presentation = {
                        fileName: result.key,
                        name: result.key,
                        s3Path: `protected/${identityId}/${result.key}`,
                        updatedAt: new Date().toISOString()
                    };
                }
                
                const updatedLecture = await client.graphql({
                    variables: {
                        input: {
                            id: selectedLectureForDocuments,
                            ...updateField
                        }
                    },
                    query: /* GraphQL */ `
                    mutation UpdateLecture($input: UpdateLectureInput!) {
                        updateLecture(input: $input) {
                            id
                            pdfDe {
                                fileName
                                name
                                s3Path
                                updatedAt
                            }
                            pdfEn {
                                fileName
                                name
                                s3Path
                                updatedAt
                            }
                            presentation {
                                fileName
                                name
                                s3Path
                                updatedAt
                            }
                        }
                    }
                    `
                });
                
                // Update the lecture in the local state
                setLectures(lectures.map(lecture => 
                    lecture.id === selectedLectureForDocuments 
                        ? {
                            ...lecture, 
                            ...(fileType === 'De' || fileType === 'En' 
                                ? { [`pdf${fileType}`]: updatedLecture.data.updateLecture[`pdf${fileType}`] }
                                : { presentation: updatedLecture.data.updateLecture.presentation })
                          } 
                        : lecture
                ));
                setShownLectures(shownLectures.map(lecture =>
                    lecture.id === selectedLectureForDocuments

                        ? {
                            ...lecture,
                            ...(fileType === 'De' || fileType === 'En'
                                ? { [`pdf${fileType}`]: updatedLecture.data.updateLecture[`pdf${fileType}`] }
                                : { presentation: updatedLecture.data.updateLecture.presentation })
                        }
                        : lecture
                ));
                
                setIsUploadingLectureFiles({...isUploadingLectureFiles, [fileType]: false});
                setUploadingLecturesErrors({...uploadingLecturesErrors, [fileType]: null});
                
            } catch (error) {
                console.log("Error uploading file:", error);
                captureError(error, {
                    feature: 'event-management-admin',
                    operation: 'upload-lecture-file',
                    consoleMessage: 'Error uploading file',
                    context: { fileType, lectureId: selectedLectureForDocuments }
                });
                setIsUploadingLectureFiles({...isUploadingLectureFiles, [fileType]: false});
                setUploadingLecturesErrors({
                    ...uploadingLecturesErrors,
                    [fileType]: error.errors?.[0].message || error.message
                });
            }
        };

    const downloadFile = async (fileName, path) => {
        try {
            const regex = /protected\/(eu-central-1:[a-z0-9-]+)\//i;
            const match = path.match(regex);
            const identity = match[1];
            
            const getUrlResult = await getUrl({
                key: fileName,
                options: {
                    accessLevel: 'protected',
                    targetIdentityId: identity,
                },
            });
            
            console.log('Signed URL: ', getUrlResult.url);
            window.open(getUrlResult.url, '_blank');
        } catch (error) {
            console.log("Error downloading file:", error);
            captureError(error, {
                feature: 'event-management-admin',
                operation: 'download-lecture-file',
                consoleMessage: 'Error downloading file',
                context: { path }
            });
        }
    };

    const removeFile = async (fileName, path, fileType, lectureId) => {
        try {
            await remove({ key: fileName, options: { accessLevel: 'protected' } });
            
            // Update field based on fileType
            let updateField = {};
            if (fileType === 'De' || fileType === 'En') {
                updateField[`pdf${fileType}`] = null;
            } else if (fileType === 'Presentation') {
                updateField.presentation = null;
            }
            
            // Update the lecture in the database
            const updatedLecture = await client.graphql({
                variables: {
                    input: {
                        id: lectureId,
                        ...updateField
                    }
                },
                query: /* GraphQL */ `
                mutation UpdateLecture($input: UpdateLectureInput!) {
                    updateLecture(input: $input) {
                        id
                        pdfDe {
                            fileName
                            name
                            s3Path
                        }
                        pdfEn {
                            fileName
                            name
                            s3Path
                        }
                        presentation {
                            fileName
                            name
                            s3Path
                        }
                    }
                }
                `
            });
            
            // Update the lecture in the local state
            setLectures(lectures.map(lecture => 
                lecture.id === lectureId 
                    ? {
                        ...lecture,
                        ...(fileType === 'De' ? { pdfDe: null } : {}),
                        ...(fileType === 'En' ? { pdfEn: null } : {}),
                        ...(fileType === 'Presentation' ? { presentation: null } : {})
                        } 
                    : lecture
            ));

            setShownLectures(shownLectures.map(lecture =>
                lecture.id === lectureId
                    ? {
                        ...lecture,
                        ...(fileType === 'De' ? { pdfDe: null } : {}),
                        ...(fileType === 'En' ? { pdfEn: null } : {}),
                        ...(fileType === 'Presentation' ? { presentation: null } : {})
                    }
                    : lecture
            ));
            
        } catch (error) {
            console.log("Error removing file:", error);
            captureError(error, {
                feature: 'event-management-admin',
                operation: 'remove-lecture-file',
                consoleMessage: 'Error removing file',
                context: { path, lectureId, fileType }
            });
        }
    };

    return (
        <><MDBCard className="w-auto">
            <MDBCardBody className="flex-column">
                <MDBCardTitle>
                    {t("Lectures")}
                    <MDBBtn 
                            className='mx-2 my-0 py-0' color='tertiary' rippleColor='light'
                            onClick={() => {
                              setEditingLecture(null);
                              setIsLectureFormOpen(true);
                            }}
                          >
                            <MDBIcon fas icon="plus" className="me-2" /> 
                            {t("Create Lecture")}
                          </MDBBtn>
                          <MDBBtn
                    color="tertiary"
                    rippleColor="light" 
                    className="mb-3 ms-2 float-end"
                    onClick={handleDownloadExcel}
                    disabled={isLoading || shownLectures.length === 0}
                >
                    <MDBIcon fas icon="file-excel" className="me-2" />
                    {t("Download Excel")}
                </MDBBtn>
                </MDBCardTitle>
                <MDBTabs className='mb-3'>
                    {
                        events.length > 0 &&
                        events.map((event, index) => (
                            <MDBTabsItem>
                            <MDBTabsLink key={index} onClick={() => handleEventTabClick(event.id)} active={selectedEvent === event.id}>
                                {event.name.find(n => n.language === i18n.language)?.text || event.name[0].text}
                            </MDBTabsLink>
                            </MDBTabsItem>
                        ))
                    }   
                </MDBTabs>
                
                <MDBTabsContent>
                    <MDBTabsPane open={basicActive === 'tab1'}>
                    <MDBRow>
                        <MDBCol md="6">
                        <MDBInput type="text" onChange={handleInputSearch} value={searchTerm} label={t("Search")} />
                        </MDBCol>
                        <MDBCol size="6" md="3">
                        <MDBDatepicker
                            label={t("Start Date")}
                            inline
                            disableFuture
                            defaultValue={filters.createdAtStart && new Date(filters.createdAtStart).toLocaleDateString('en-GB')}
                            value={filters.createdAtStart && new Date(filters.createdAtStart).toLocaleDateString('en-GB')}
                            onChange={date => date && setFilters({ ...filters, createdAtStart: new Date(date.replace(/(\d{2})\/(\d{2})\/(\d{4})/, '$3-$2-$1')).toISOString()})}
                          />
                        </MDBCol>
                        <MDBCol size="6" md="3">
                        <MDBDatepicker
                            label={t("End Date")}
                            inline
                            disableFuture
                            defaultValue={filters.createdAtEnd && new Date(filters.createdAtEnd).toLocaleDateString('en-GB')}
                            value={filters.createdAtEnd && new Date(filters.createdAtEnd).toLocaleDateString('en-GB')}
                            onChange={date => date && setFilters({ ...filters, createdAtEnd: new Date(date.replace(/(\d{2})\/(\d{2})\/(\d{4})/, '$3-$2-$1')).toISOString()})}
                          />
                        </MDBCol>
                    </MDBRow>
                    
                    
                    <MDBRow className="mt-2 mt-xl-3">
                        <MDBCol md="3">
                            <MDBSelect label={t("Type")}
                                onChange={type => type && setFilters({ ...filters, type: type.value})}
                                value={filters.type}
                                data={
                                    [
                                        { text: t("All"), value: null },
                                        { text: t("Marketing"), value: "marketing" },
                                        { text: t("Technical"), value: "technical" },
                                        { text: t("Educational"), value: "educational" },
                                        { text: t("Recruiting"), value: "recruiting" },
                                        { text: t("VIP"), value: "vip" },
                                        { text: t("Promotion Marketing"), value: "promotion_marketing" },
                                        { text: t("Promotion Technical"), value: "promotion_technical" }
                                    ]
                                }
                            />
                        </MDBCol>
                        <MDBCol md="3">
                            <MDBSelect label={t("Status")}
                                onChange={selected => selected && setFilters({ ...filters, status: selected.value})}
                                value={filters.status}
                                data={
                                    [   
                                        { text: t("All"), value: null },
                                        { text: t("Open"), value: "created" },
                                        { text: t("Approved"), value: "checked" },
                                        { text: t("Not Approved"), value: "not_approved" },
                                        { text: t("In Planning"), value: "in_planning" },
                                        { text: t("Committed"), value: "committed" },
                                    ]
                                }
                            />
                        </MDBCol>
                        <MDBCol md="3">
                            <MDBSelect label={t("Errors")}
                                onChange={selected => filterbyHasErrors(selected.map(s => s.value))}
                                multiple
                                data={
                                    [
                                        { text: "Titel", value: "Titel" },
                                        { text: "Referenten", value: "Referenten" },
                                        { text: "Unterpunkte", value: "Unterpunkte" },
                                        { text: "Beschreibung", value: "Beschreibung" },
                                        { text: "Bedingungen", value: "Bedingungen" },
                                        { text: "Bild", value: "Bild" },
                                        { text: "Themenbereich", value: "Themenbereich" }
                                    ]
                                }
                            />
                        </MDBCol>
                        <MDBCol md="2">
                            <MDBCheckbox label={t("Nach Raum sortieren")} checked={sortByRoom} onChange={e => setSortByRoom(e.target.checked)} />
                        </MDBCol>
                        <MDBCol md="1">
                        <MDBIcon className="float-end" fas icon="sync-alt" onClick={() =>  getLectures(selectedEvent)} />
                        </MDBCol>

                        
                    </MDBRow>
                    
                        <MDBTable>
                            <MDBTableHead>
                                <tr>
                                    <th width="125">{t("Datum")} 
                                    <br />
                                    <span className="text-muted">
                                    {t("Erstellt")} <MDBIcon fas icon="long-arrow-alt-up" size='sm' onClick={() => setFilters({...filters, createdAtSort: "desc", updatedAtSort: null})} /> <MDBIcon fas icon="long-arrow-alt-down" size='sm' onClick={() => setFilters({...filters, createdAtSort: "asc", updatedAtSort: null})} />
                                    </span>
                                    <br />
                                    <span className="text-muted">
                                    {t("Geändert")} <MDBIcon fas icon="long-arrow-alt-up" size='sm' onClick={() => setFilters({...filters, updatedAtSort: "desc", createdAtSort: null})} /> <MDBIcon fas icon="long-arrow-alt-down" size='sm' onClick={() => setFilters({...filters, updatedAtSort: "asc", createdAtSort: null})} />
                                    </span>
                                    </th>
                                    <th>{t("Company")}</th>
                                    <th width="100">{t("Type")}</th>
                                    <th>{t("Title")}</th>
                                    <th>{t("Speakers")}</th>
                                    <th>{t("Errors")}</th>
                                    <th>{t("Status")}</th>
                                    <th>{t("Presentation")}</th>
                                    <th>{t("Video")}</th>
                                    <th>{t("Actions")}</th>
                                </tr>
                            </MDBTableHead>
                            <MDBTableBody>
                                { isLoading ? <tr><td colSpan="8" className="text-center"><div className="spinner-border" role="status"><span className="visually-hidden">Loading...</span></div></td></tr>
                                : shownLectures.length > 0 ?
                                    shownLectures.map((lecture, index) => {
                                        return (
                                            <tr key={index}>
                                                <td className="px-1">
                                                {new Date(lecture.createdAt).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' })}
                                                {new Date(lecture.createdAt).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' }) !== new Date(lecture.updatedAt).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' }) && <>
                                                    <br />
                                                <span className="text-success" style={{fontSize: "smaller"}}>{new Date(lecture.updatedAt).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' })}</span>
                                                </>}
                                                </td>
                                                <td width="300" className="px-1"><>
                                                    {
                                                        lecture.company ? (lecture.company?.image?.main?.fileName ?
                                                        <div className='d-flex align-items-center'>
                                                            {lecture.company.image?.main?.fileName ?
                                                                <img
                                                                    src={"https://iec-cp-public.s3.eu-central-1.amazonaws.com/companyImages/"+lecture.company.image.main.fileName}
                                                                    className="rounded-circle border"
                                                                    style={{ height: "50px", width: "50px", objectFit: "contain" }}
                                                                    alt={lecture.company.image.alt}
                                                                />
                                                            :
                                                                <MDBIcon className="rounded-circle border" style={{ height: "50px", width: "50px", textAlign: "center", paddingTop: "12px", fontSize: "20px" }} fas icon="company-alt" />
                                                                        
                                                            }
                                                            <div className='ms-3'>
                                                            <p className='fw-bold mb-1'>{lecture.company.name}</p>
                                                            <p className='text-muted mb-0'>{lecture.companyBooth}</p>
                                                            </div>
                                                        </div>
                                                        :
                                                        <MDBIcon
                                                            className="rounded-circle border"
                                                            style={{
                                                                height: "50px",
                                                                width: "50px",
                                                                textAlign: "center",
                                                                paddingTop: "12px",
                                                                fontSize: "20px",
                                                            }}
                                                            fas
                                                            icon="user-alt"
                                                        />
                                                        )
                                                        :
                                                        lecture.mainSpeakerUser && 
                                                        (lecture.mainSpeakerUser?.image?.fileName ?
                                                        <div className='d-flex align-items-center'>
                                                            {lecture.mainSpeakerUser.image?.fileName ?
                                                                <img
                                                                    src={"https://iec-cp-public.s3.eu-central-1.amazonaws.com/userImages/"+lecture.mainSpeakerUser.image.fileName}
                                                                    className="rounded-circle border"
                                                                    style={{ height: "50px", width: "50px", objectFit: "contain" }}
                                                                    alt={lecture.mainSpeakerUser.image.alt}
                                                                />
                                                            :
                                                                <MDBIcon className="rounded-circle border" style={{ height: "50px", width: "50px", textAlign: "center", paddingTop: "12px", fontSize: "20px" }} fas icon="user-alt" />
                                                                        
                                                            }
                                                            <div className='ms-3'>
                                                            <p className='fw-bold mb-1'>{lecture.mainSpeakerUser.firstName} {lecture.mainSpeakerUser.lastName}</p>
                                                            <p className='text-muted mb-0'>{lecture.mainSpeakerUser.email}</p>
                                                            </div>
                                                        </div>
                                                        :
                                                        <MDBIcon
                                                            className="rounded-circle border"
                                                            style={{
                                                                height: "50px",
                                                                width: "50px",
                                                                textAlign: "center",
                                                                paddingTop: "12px",
                                                                fontSize: "20px",
                                                            }}
                                                            fas
                                                            icon="user-alt"
                                                        />
                                                        )
                                                    }
                                                </>
                                                </td>
                                                <td className="px-1">{t(lecture.type)}</td>
                                                <td className="px-1">{lecture.title?.find(t => t.language === i18n.language)?.text || lecture.title?.[0]?.text}</td>
                                                <td className="px-1">
                                                    {(() => {
                                                        // Speakers are already unified (employee-based and user-based combined)
                                                        const allSpeakers = (lecture.speakers?.items || []).map(s => ({
                                                            image: s?.employee?.image || s?.employee?.user?.image || s?.user?.image,
                                                            firstName: s?.employee?.user?.firstName || s?.user?.firstName,
                                                            lastName: s?.employee?.user?.lastName || s?.user?.lastName
                                                        }));
                                                        const totalCount = allSpeakers.length;
                                                        const displayCount = totalCount > 4 ? 4 : totalCount;
                                                        const remainingCount = totalCount - displayCount;

                                                        return (
                                                            <div className="d-flex align-items-center">
                                                                {allSpeakers.slice(0, displayCount).map((speaker, index) => (
                                                                    <div key={index} className="me-1">
                                                                        {speaker.image?.fileName ? (
                                                                            <img
                                                                                src={`https://iec-cp-public.s3.eu-central-1.amazonaws.com/userImages/${speaker.image.fileName}`}
                                                                                className="rounded-circle border"
                                                                                style={{
                                                                                    height: "50px",
                                                                                    width: "50px",
                                                                                    objectFit: "cover",
                                                                                }}
                                                                                alt={speaker.image.alt}
                                                                                title={`${speaker.firstName} ${speaker.lastName}`}
                                                                            />
                                                                        ) : (
                                                                            <MDBIcon
                                                                                className="rounded-circle border"
                                                                                style={{
                                                                                    height: "50px",
                                                                                    width: "50px",
                                                                                    textAlign: "center",
                                                                                    paddingTop: "12px",
                                                                                    fontSize: "20px",
                                                                                }}
                                                                                fas
                                                                                icon="user-alt"
                                                                                title={`${speaker.firstName} ${speaker.lastName}`}
                                                                            />
                                                                        )}
                                                                    </div>
                                                                ))}
                                                                {remainingCount > 0 && (
                                                                    <small className="text-muted">+{remainingCount}</small>
                                                                )}
                                                            </div>
                                                        );
                                                    })()}
                                                </td>
                                                <td width="0">
                                                    {(() => {
                                                        // Only show validation results for lectures from the specific event
                                                        const targetEventId = "cb7a916e-ed71-483a-8177-22532ab0b237";
                                                        const shouldShowValidation = lecture.eventLecturesId === targetEventId;
                                                        
                                                        if (!shouldShowValidation) {
                                                            return null;
                                                        }
                                                        
                                                        const lectureErrors = getLectureValidationErrors(lecture.id);
                                                        const isValid = isLectureValid(lecture.id);
                                                        
                                                        return isValid ? (
                                                            <div className="text-success">
                                                                <MDBIcon fas icon="check-circle" className="me-1" />
                                                                <small>{t("Complete")}</small>
                                                            </div>
                                                        ) : (
                                                            <div className="text-warning">
                                                                <div className="mt-1">
                                                                    {lectureErrors.map((error, index) => {
                                                                        // Translate field names to user-friendly labels
                                                                        const getFieldLabel = (field) => {
                                                                            const fieldMap = {
                                                                                'generalConditions': t('General Conditions'),
                                                                                'speakers': t('Speakers'),
                                                                                'topics.0': t('Topics'),
                                                                                'topics': t('Topics'),
                                                                                'subjects': t('Subjects'),
                                                                                'handout': t('Handout'),
                                                                                'title': t('Title'),
                                                                                'description': t('Description'),
                                                                                'content': t('Content'),
                                                                                'type': t('Type'),
                                                                                'duration': t('Duration'),
                                                                                'targetAudience': t('Target Audience'),
                                                                                'prerequisites': t('Prerequisites'),
                                                                                'learningGoals': t('Learning Goals'),
                                                                                'abstract': t('Abstract'),
                                                                                'biography': t('Biography'),
                                                                                'conditions': t('Conditions'),
                                                                                "image": t("Image")
                                                                            };
                                                                            return fieldMap[field] || field;
                                                                        };

                                                                        const getMessageText = (message, field) => {
                                                                            // Handle legacy/technical error messages
                                                                            if (message === 'Required') {
                                                                                return t('Missing');
                                                                            }
                                                                            if (message === 'Expected array, received object') {
                                                                                if (field === 'speakers') {
                                                                                    return t('Data structure issue (speakers.items)');
                                                                                }
                                                                                return t('Wrong data type (expected list)');
                                                                            }
                                                                            if (message === 'Expected string, received object') {
                                                                                if (field === 'topics.0' || field.startsWith('topics.')) {
                                                                                    return t('Data structure issue (expected text)');
                                                                                }
                                                                                return t('Wrong data type (expected text)');
                                                                            }
                                                                            
                                                                            // Handle new validation messages - reusing the same mapping from user AllLectures
                                                                            const messageMap = {
                                                                                // Image messages
                                                                                'Please upload an image for the lecture': t('Please upload an image for the lecture'),
                                                                                'Image required': t('Image required'),
                                                                                
                                                                                // Speaker messages
                                                                                'At least one main speaker must be selected': t('At least one main speaker must be selected'),
                                                                                'All main speakers must have a valid employee ID': t('All main speakers must have a valid employee ID'),
                                                                                
                                                                                // Title messages
                                                                                'Title in German and English required': t('Title in German and English required'),
                                                                                'Lecture type required': t('Lecture type required'),
                                                                                'Valid lecture type required': t('Valid lecture type required'),
                                                                                
                                                                                // Content messages
                                                                                'Description in German and English required': t('Description in German and English required'),
                                                                                
                                                                                // Subjects messages
                                                                                'At least 3 subpoints required': t('At least 3 subpoints required'),
                                                                                'Maximum 5 subpoints allowed': t('Maximum 5 subpoints allowed'),
                                                                                'At least 3 subpoints must be completely filled in (both languages)': t('At least 3 subpoints must be completely filled in (both languages)'),
                                                                                'When a subpoint is filled in, both languages (German and English) must be filled in': t('When a subpoint is filled in, both languages (German and English) must be filled in'),
                                                                                
                                                                                // Outline messages
                                                                                'All outline point titles must be filled in': t('All outline point titles must be filled in'),
                                                                                
                                                                                // Conditions messages
                                                                                'Condition must be accepted': t('Condition must be accepted'),
                                                                                'All required general conditions must be accepted': t('All required general conditions must be accepted'),
                                                                                'Invalid general condition': t('Invalid general condition'),
                                                                                'General conditions': t('General conditions'),
                                                                                'All required conditions must be accepted': t('All required conditions must be accepted'),
                                                                                'Invalid condition for this lecture type': t('Invalid condition for this lecture type'),
                                                                                'Conditions': t('Conditions'),
                                                                                
                                                                                // Topics messages
                                                                                'Event location required': t('Event location required'),
                                                                                'Target audience required': t('Target audience required'),
                                                                                'At least 1 participant': t('At least 1 participant'),
                                                                                'Prerequisites required': t('Prerequisites required'),
                                                                                'Valid meeting link required': t('Valid meeting link required'),
                                                                                'Recording permission required': t('Recording permission required'),
                                                                                
                                                                                // Handout messages
                                                                                'At least one handout (German or English) must be uploaded': t('At least one handout (German or English) must be uploaded'),
                                                                                
                                                                                // Language and text validation
                                                                                'Language is required': t('Language is required'),
                                                                                'Text is required': t('Text is required')
                                                                            };
                                                                            
                                                                            // Check if we have a direct translation
                                                                            if (messageMap[message]) {
                                                                                return messageMap[message];
                                                                            }
                                                                            
                                                                            // Fallback: return the original message
                                                                            return message;
                                                                        };

                                                                        return (
                                                                            <div key={index} className="small text-danger mb-1 d-flex align-items-center">
                                                                                <MDBIcon fas icon="times-circle" size="sm" className="me-1" />
                                                                                <strong>{getFieldLabel(error.field)}:</strong>
                                                                                <span className="ms-1">{getMessageText(error.message, error.field)}</span>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </div>
                                                        );
                                                    })()}
                                                    {lecture.adminComment && lecture.adminComment.length > 2 && (
                                                        <div className="mt-2 p-2 bg-light border rounded">
                                                            <small className="text-info d-block mb-1">
                                                                <MDBIcon fas icon="comment" className="me-1" />
                                                                {t("Admin Comment")}:
                                                            </small>
                                                            <small className="text-dark">{lecture.adminComment}</small>
                                                        </div>
                                                    )}
                                                </td>
                                                <td>
                                                {lecture.status === "created" ? null : t(lecture.status === "checked" ? "✅" : lecture.status)}
                                                </td>
                                                <td className="px-1">
                                                    {lecture.room && <p className="mb-0">{t("Room") + " " + lecture.room}</p>}
                                                    {lecture.startTime && <p className="mb-0">{new Date(lecture.startTime).toLocaleDateString('de-DE')}</p>}
                                                    {lecture.startTime && <p className="mb-0">{new Date(lecture.startTime).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr</p>}
                                                    <div className="mt-2">
                                                        {/* <span>PDF:</span> */}
                                                        <div className="cursor-pointer" onClick={() => handleOpenDocumentUploadModal(lecture.id)}>
                                                            PDF: 
                                                            DE
                                                            {lecture.pdfDe?.fileName ? 
                                                                <MDBIcon fas icon="check" className="text-success me-1" title={t("German document available")} />
                                                                : 
                                                                <MDBIcon fas icon="times" className="text-danger me-1" title={t("No German document")} />
                                                            }
                                                            EN
                                                            {lecture.pdfEn?.fileName ? 
                                                                <MDBIcon fas icon="check" className="text-success me-1" title={t("English document available")} />
                                                                : 
                                                                <MDBIcon fas icon="times" className="text-danger me-1" title={t("No English document")} />
                                                            }
                                                           
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <div className="cursor-pointer" onClick={() => handleOpenPresentationUploadModal(lecture.id)}>
                                                            PPTX: 
                                                            {lecture.presentation?.fileName ? 
                                                                <>
                                                                <MDBIcon fas icon="check" className="text-success me-1" title={t("Presentation available")} />
                                                                <br />
                                                                {lecture.presentation.updatedAt && <span className="text-muted">{isoToGermanDate(lecture.presentation.updatedAt)}</span>}
                                                                </>
                                                                : 
                                                                <MDBIcon fas icon="times" className="text-danger me-1" title={t("No presentation")} />
                                                            }
                                                            
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <MDBIcon fas icon="edit" style={{ color: "link", cursor: "pointer" }} onClick={() => hanleOpenVideoModal(lecture.id)} />
                                                     <br></br>
                                                    {
                                                        lecture.videoS3Key?.s3Path &&
                                                        <MDBIcon fas style={{ color: "green" }} icon="video" />
                                                    }
                                               
                                               </td>
                                                <td>
                                                    <MDBBtnGroup shadow="0">
                                                        {/* <MDBBtn color='link' style={{color: "black"}} className="px-2" >{t("Upload Documents")}</MDBBtn> */}
                                                        <div className="justify-content-between">
                                                        <Link to={`/admin/lecture/${lecture.company?.id}/${lecture.id}`}>
                                                            <MDBIcon color='link' fas icon="edit" />
                                                        </Link>
                                                        <MDBIcon
                                                            fas
                                                            icon="trash"
                                                            style={{ color: "red", cursor: "pointer" }} className="ms-3" onClick={() => { setSelectedLectureRemove(lecture.id); toggleDeleteLectureModal() }}
                                                        />
                                                       
                                                        </div>
                                                        
                                                    </MDBBtnGroup>
                                                </td>
                                            </tr>
                                        )
                                    })
                                    :
                                    <tr>
                                        <td colSpan="7">
                                            {t("No lectures found")}
                                        </td>
                                    </tr>
                                }
                            </MDBTableBody>
                        </MDBTable>
                    </MDBTabsPane>
                </MDBTabsContent>
            </MDBCardBody>
        </MDBCard>

            <MDBModal open={deleteLectureModal} onClose={() => setDeleteLectureModal(false)}>
                <MDBModalDialog centered size="sm">
                    <MDBModalContent>
                        <MDBModalHeader>
                            <MDBModalTitle>{t("Remove Lecture")}</MDBModalTitle>
                            <MDBBtn className='btn-close' color='none' onClick={toggleDeleteLectureModal}></MDBBtn>
                        </MDBModalHeader>
                        <MDBModalBody className="text-center">
                            <p>{t("Are you sure you want to remove this lecture?")}</p>
                            <p className="fw-bold">{selectedLectureRemove && (lectures.find(l => l.id === selectedLectureRemove)?.title.find(t => t.language === i18n.language)?.text || lectures.find(l => l.id === selectedLectureRemove).title[0].text)}</p>
                            <MDBBtn color='danger' onClick={() => deleteLecture(selectedLectureRemove)}>{t("Remove")}</MDBBtn>
                        </MDBModalBody>
                    </MDBModalContent>
                </MDBModalDialog>
            </MDBModal>

<MDBModal open={videoModal} onClose={() => setVideoModal(false)}>
    <MDBModalDialog centered size="lg">
        <MDBModalContent>
            <MDBModalHeader>
                <MDBModalTitle>{t("Upload Video")}</MDBModalTitle>
                <MDBBtn className='btn-close' color='none' onClick={() => {
                    setLectureVideosUploadStatus({});
                    setSelectedLectureVideo(null);
                    inputFieldRef.current.value = "";
                    setSelectedVideoUrl(null);
                    toggleVideoModal();
                }}></MDBBtn>
            </MDBModalHeader>
            <MDBModalBody className="text-center">
                <h6>
                    {selectedLectureVideo && (lectures.find(l => l.id === selectedLectureVideo)?.title.find(t => t.language === i18n.language)?.text || lectures.find(l => l.id === selectedLectureVideo).title[0].text)}
                </h6>
                {
                    selectedVideoUrl &&
                    <ReactPlayer
                        url={selectedVideoUrl}
                        controls
                        width="100%"
                        height="auto"
                    />

                }
                <MDBRow>
                    <MDBCol size="11">
                        <MDBInput
                        type="file" 
                        onChange={e => uploadVideo(e.target.files[0], selectedLectureVideo)}
                        ref={inputFieldRef}
                        />
                    </MDBCol>
                    <MDBCol size="1">
                        <MDBIcon icon="trash" color="danger" className="mt-2" style={{cursor: "pointer"}} onClick={() => removeVideo(selectedLectureVideo)} />
                    </MDBCol>

                </MDBRow>
               
               
                {lectureVideosUploadStatus[selectedLectureVideo] && <>
                    <MDBProgress height="20" className="mt-4">
                        <MDBProgressBar striped bgColor='success' width={lectureVideosUploadStatus[selectedLectureVideo].percent} valuemin={0} valuemax={100}>
                            
                        </MDBProgressBar>
                    </MDBProgress>
                    <p>
                        {t("Uploaded")}: {lectureVideosUploadStatus[selectedLectureVideo].percent}% ({lectureVideosUploadStatus[selectedLectureVideo].speed})
                    </p>
                </>}
            </MDBModalBody>
        </MDBModalContent>
    </MDBModalDialog>
</MDBModal>
<MDBModal
      open={isLectureFormOpen}
      tabIndex="-1"
      backdrop={true}
      size="lg"
    >
      <MDBModalDialog size="lg">
        <MDBModalContent>
          <MDBModalHeader>
            <MDBModalTitle>
              {editingLecture ? t("Edit Lecture") : t("Create Lecture")}
            </MDBModalTitle>
            <MDBBtn 
              className='btn-close' 
              color='none' 
              onClick={() => {
                setIsLectureFormOpen(false);
                setEditingLecture(null);
              }}
            ></MDBBtn>
          </MDBModalHeader>
          <MDBModalBody>
            <LectureForm
              lectureId={editingLecture?.id}
              eventId={selectedEvent}
              onSave={(savedLecture) => {
                // Add the new lecture to both lectures and shownLectures arrays
                const newLecture = {
                  ...savedLecture,
                  startTime: null,
                  endTime: null,
                  room: null,
                  createdAt: new Date().toISOString(),
                  updatedAt: new Date().toISOString(),
                };
                console.log("newLecture", newLecture);
                if (editingLecture) {
                  // Update existing lecture
                  setLectures(prev => prev.map(l => l.id === savedLecture.id ? newLecture : l));
                  setShownLectures(prev => prev.map(l => l.id === savedLecture.id ? newLecture : l));
                } else {
                  // Add new lecture
                  setLectures(prev => [newLecture, ...prev]);
                  setShownLectures(prev => [newLecture, ...prev]);
                }

                setIsLectureFormOpen(false);
                setEditingLecture(null);
              }}
              onCancel={() => {
                setIsLectureFormOpen(false);
                setEditingLecture(null);
              }}
            />
          </MDBModalBody>
        </MDBModalContent>
      </MDBModalDialog>
    </MDBModal>

    <MDBModal open={documentUploadModal} onClose={toggleDocumentUploadModal}>
    <MDBModalDialog centered size="lg">
        <MDBModalContent>
            <MDBModalHeader>
                <MDBModalTitle>{t("Upload Lecture Documents")}</MDBModalTitle>
                <MDBBtn className='btn-close' color='none' onClick={toggleDocumentUploadModal}></MDBBtn>
            </MDBModalHeader>
            <MDBModalBody>
                {selectedLectureForDocuments && (
                    <>
                        <h5>
                            {lectures.find(l => l.id === selectedLectureForDocuments)?.title?.find(t => t.language === i18n.language)?.text || 
                            lectures.find(l => l.id === selectedLectureForDocuments)?.title?.[0]?.text}
                        </h5>
                        
                        <p className="text-muted mb-4">
                            {t("The documents will be made available to the participants of the event. Deadline for submission")}{": "}
                            {isoToGermanDate(lectures.find(l => l.id === selectedLectureForDocuments)?.event?.lectureDocumentsEndDate)}
                        </p>
                        
                        {/* German Document Upload */}
                        <div className="mb-4">
                            <h6>{t("German Document")}</h6>
                            <div className="d-flex align-items-center mb-2">
                                <div className="me-2">
                                    <ReactCountryFlag
                                        countryCode="de"
                                        svg
                                        style={{
                                            width: "20px",
                                            height: "20px",
                                        }}
                                    />
                                </div>
                                
                                {lectures.find(l => l.id === selectedLectureForDocuments)?.pdfDe?.fileName ? (
                                    <div className="d-flex flex-column">
                                        <MDBBtn 
                                            color="link" 
                                            onClick={() => downloadFile(
                                                lectures.find(l => l.id === selectedLectureForDocuments).pdfDe.fileName,
                                                lectures.find(l => l.id === selectedLectureForDocuments).pdfDe.s3Path
                                            )} 
                                            size="sm" 
                                            className="mb-1"
                                        >
                                            {lectures.find(l => l.id === selectedLectureForDocuments).pdfDe.fileName}
                                        </MDBBtn>
                                        
                                        <MDBBtn 
                                            color="link" 
                                            style={{color: "red"}} 
                                            size="sm" 
                                            onClick={() => removeFile(
                                                lectures.find(l => l.id === selectedLectureForDocuments).pdfDe.fileName,
                                                lectures.find(l => l.id === selectedLectureForDocuments).pdfDe.s3Path,
                                                'De',
                                                selectedLectureForDocuments
                                            )}
                                        >
                                            {t("Remove")}
                                        </MDBBtn>
                                    </div>
                                ) : (
                                    <MDBFile id='fileDocDe' onChange={e => handleSetFile(e.target.files[0], 'De')} />
                                )}
                                
                                {isUploadingLectureFiles.De && <MDBSpinner size='sm' role='status' tag='span' className='ms-2' />}
                            </div>
                            
                            {uploadingLecturesErrors.De && (
                                <div className="text-danger small mb-2">{uploadingLecturesErrors.De}</div>
                            )}
                        </div>
                        
                        {/* English Document Upload */}
                        <div>
                            <h6>{t("English Document")}</h6>
                            <div className="d-flex align-items-center mb-2">
                                <div className="me-2">
                                    <ReactCountryFlag
                                        countryCode="gb"
                                        svg
                                        style={{
                                            width: "20px",
                                            height: "20px",
                                        }}
                                    />
                                </div>
                                
                                {lectures.find(l => l.id === selectedLectureForDocuments)?.pdfEn?.fileName ? (
                                    <div className="d-flex flex-column">
                                        <MDBBtn 
                                            color="link" 
                                            onClick={() => downloadFile(
                                                lectures.find(l => l.id === selectedLectureForDocuments).pdfEn.fileName,
                                                lectures.find(l => l.id === selectedLectureForDocuments).pdfEn.s3Path
                                            )} 
                                            size="sm" 
                                            className="mb-1"
                                        >
                                            {lectures.find(l => l.id === selectedLectureForDocuments).pdfEn.fileName}
                                        </MDBBtn>
                                        
                                        <MDBBtn 
                                            color="link" 
                                            style={{color: "red"}} 
                                            size="sm" 
                                            onClick={() => removeFile(
                                                lectures.find(l => l.id === selectedLectureForDocuments).pdfEn.fileName,
                                                lectures.find(l => l.id === selectedLectureForDocuments).pdfEn.s3Path,
                                                'En',
                                                selectedLectureForDocuments
                                            )}
                                        >
                                            {t("Remove")}
                                        </MDBBtn>
                                    </div>
                                ) : (
                                    <MDBFile id='fileDocEn' onChange={e => handleSetFile(e.target.files[0], 'En')} />
                                )}
                                
                                {isUploadingLectureFiles.En && <MDBSpinner size='sm' role='status' tag='span' className='ms-2' />}
                            </div>
                            
                            {uploadingLecturesErrors.En && (
                                <div className="text-danger small mb-2">{uploadingLecturesErrors.En}</div>
                            )}
                        </div>
                    </>
                )}
            </MDBModalBody>
        </MDBModalContent>
    </MDBModalDialog>
</MDBModal>
<MDBModal open={presentationUploadModal} onClose={togglePresentationUploadModal}>
    <MDBModalDialog centered size="lg">
        <MDBModalContent>
            <MDBModalHeader>
                <MDBModalTitle>{t("Upload Presentation")}</MDBModalTitle>
                <MDBBtn className='btn-close' color='none' onClick={togglePresentationUploadModal}></MDBBtn>
            </MDBModalHeader>
            <MDBModalBody>
                {selectedLectureForDocuments && (
                    <>
                        <h5>
                            {lectures.find(l => l.id === selectedLectureForDocuments)?.title?.find(t => t.language === i18n.language)?.text || 
                            lectures.find(l => l.id === selectedLectureForDocuments)?.title?.[0]?.text}
                        </h5>
                        
                        <p className="text-muted mb-4">
                            {t("Please upload your presentation for this lecture. Preferably a PowerPoint file, but other document formats are also accepted. Deadline for submission")}{": "}
                            {isoToGermanDate(lectures.find(l => l.id === selectedLectureForDocuments)?.event?.lecturePresentationEndDate)}
                        </p>
                        
                        <div className="mb-4">
                            <h6>{t("Presentation File")}</h6>
                            <div className="d-flex align-items-center mb-2">
                                {lectures.find(l => l.id === selectedLectureForDocuments)?.presentation?.fileName ? (
                                    <div className="d-flex flex-column">
                                        <MDBBtn 
                                            color="link" 
                                            onClick={() => downloadFile(
                                                lectures.find(l => l.id === selectedLectureForDocuments).presentation.fileName,
                                                lectures.find(l => l.id === selectedLectureForDocuments).presentation.s3Path
                                            )} 
                                            size="sm" 
                                            className="mb-1"
                                        >
                                            {lectures.find(l => l.id === selectedLectureForDocuments).presentation.fileName}
                                        </MDBBtn>
                                        
                                        <MDBBtn 
                                            color="link" 
                                            style={{color: "red"}} 
                                            size="sm" 
                                            onClick={() => removeFile(
                                                lectures.find(l => l.id === selectedLectureForDocuments).presentation.fileName,
                                                lectures.find(l => l.id === selectedLectureForDocuments).presentation.s3Path,
                                                'Presentation',
                                                selectedLectureForDocuments
                                            )}
                                        >
                                            {t("Remove")}
                                        </MDBBtn>
                                    </div>
                                ) : (
                                    <MDBFile id='filePresentation' onChange={e => handleSetFile(e.target.files[0], 'Presentation')} />
                                )}
                                
                                {isUploadingLectureFiles.Presentation && <MDBSpinner size='sm' role='status' tag='span' className='ms-2' />}
                            </div>
                            
                            {uploadingLecturesErrors.Presentation && (
                                <div className="text-danger small mb-2">{uploadingLecturesErrors.Presentation}</div>
                            )}
                        </div>
                    </>
                )}
            </MDBModalBody>
        </MDBModalContent>
    </MDBModalDialog>
</MDBModal>
        </>
    )
}
